// Single source of truth for translating between the browser URL and the
// community page's view state.
//
// State shape:
//   { tab: 'explore'|'favorites', q: string, tags: string[],
//     author: string, sort: 'exports'|'popular'|'recent'|'stars'|'name', page: number }

export const DEFAULTS = Object.freeze({
  tab: 'explore',
  q: '',
  tags: [],
  author: '',
  sort: 'exports',
  page: 1
});

const VALID_SORTS = new Set(['exports', 'popular', 'recent', 'stars', 'name']);
const VALID_TABS  = new Set(['explore', 'favorites']);

export function readFromUrl(search) {
  const params = new URLSearchParams(search || '');
  const rawTab = params.get('tab');
  const rawSort = params.get('sort');
  const rawPage = parseInt(params.get('page') || '', 10);
  return {
    tab:    VALID_TABS.has(rawTab) ? rawTab : DEFAULTS.tab,
    q:      params.get('q') || DEFAULTS.q,
    tags:   parseTagsParam(params.get('tags')),
    author: params.get('author') || DEFAULTS.author,
    sort:   VALID_SORTS.has(rawSort) ? rawSort : DEFAULTS.sort,
    page:   Number.isFinite(rawPage) && rawPage > 0 ? rawPage : DEFAULTS.page
  };
}

export function writeToUrl(state, { mode = 'push' } = {}) {
  const search = stateToSearch(state);
  const url = search ? `${location.pathname}?${search}` : location.pathname;
  // Preserve the existing history.state so Turbo Drive's restoration
  // metadata (its `turbo` key) survives our same-page URL updates.
  // Without this, history.back() from a different page sees a "stateless"
  // entry, the popstate fires but Turbo can't restore — URL pops while the
  // page stays put.
  const stateData = history.state;
  if (mode === 'replace') history.replaceState(stateData, '', url);
  else                    history.pushState(stateData, '', url);
}

export function toApiParams(state) {
  const out = {};
  if (state.q && state.q.trim())    out.q = state.q.trim();
  if (state.tags.length > 0)        out.tags = state.tags.join(',');
  if (state.author)                 out.author = state.author;
  if (state.sort !== DEFAULTS.sort) out.sort = state.sort;
  if (state.page !== 1)             out.page = String(state.page);
  if (state.tab === 'favorites')    out.favorites = 'true';
  return out;
}

export function apiParamsToSearchString(apiParams) {
  return new URLSearchParams(apiParams).toString();
}

function parseTagsParam(raw) {
  if (!raw) return [];
  return raw.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
}

function stateToSearch(state) {
  const params = new URLSearchParams();
  if (state.tab !== DEFAULTS.tab)    params.set('tab', state.tab);
  if (state.q)                       params.set('q', state.q);
  if (state.tags.length > 0)         params.set('tags', state.tags.join(','));
  if (state.author)                  params.set('author', state.author);
  if (state.sort !== DEFAULTS.sort)  params.set('sort', state.sort);
  if (state.page > 1)                params.set('page', String(state.page));
  return params.toString();
}
